// NVIDIA DGX Spark — product page content for /enterprise/dgx-spark-malaysia/.
//
// SOURCING: every specification below comes from NVIDIA's own product page
// (nvidia.com/en-us/products/workstations/dgx-spark/, retrieved 2026-08-19).
// OEM model names are the ones NVIDIA lists on that page. Nothing here is
// inferred from a review or a retailer listing.
//
// RULES (docs/research/enterprise-brand-directory.md): no prices, no stock
// claims, no lead times. The distributor is never named. Descriptive
// "we supply {Brand}" only — no conferred titles.
//
// The honest caveat about memory bandwidth stays in. It is the single most
// useful thing we can tell a buyer, it is what a box-shifter will not say,
// and it is why a Spark is sometimes the wrong purchase.

export interface SpecRow {
  label: string;
  value: string;
}

export interface OemVariant {
  /** Manufacturer, as the buyer would search it. */
  brand: string;
  /** Exact model name as published by NVIDIA or the OEM. */
  model: string;
  /** What is distinctive about this one — never invented; omit if unknown. */
  note: string;
  /** Brand slug in the enterprise directory, for the enquiry deep link. */
  brandSlug: string;
  /**
   * The model's own page under /enterprise/, where one exists. The hub links
   * to its spokes in content — a footer link is not a hub-and-spoke structure.
   */
  page?: string;
}

/** Locked by NVIDIA across every OEM build — these never vary. */
export const dgxSparkSpecs: SpecRow[] = [
  { label: "Superchip", value: "NVIDIA GB10 Grace Blackwell" },
  { label: "CPU", value: "20-core Arm — 10× Cortex-X925 + 10× Cortex-A725" },
  { label: "GPU", value: "Blackwell architecture, 5th-gen Tensor Cores" },
  { label: "AI performance", value: "Up to 1 petaFLOP at FP4" },
  { label: "Unified memory", value: "128 GB LPDDR5x, CPU/GPU coherent" },
  { label: "Memory bandwidth", value: "273 GB/s" },
  { label: "Networking", value: "ConnectX-7 at 200 Gbps, plus 10 GbE" },
  { label: "Power", value: "240 W PSU (GB10 TDP 140 W)" },
  { label: "Size and weight", value: "150 × 150 × 50.5 mm, 1.2 kg" },
  { label: "Operating system", value: "NVIDIA DGX OS" },
];

/** What the 128 GB actually buys you, in NVIDIA's own terms. */
export const dgxSparkCapacity: SpecRow[] = [
  { label: "Inference, one unit", value: "Models up to 200 billion parameters" },
  { label: "Fine-tuning, one unit", value: "Models up to 70 billion parameters" },
  { label: "Two units linked", value: "Models up to 405 billion parameters" },
];

/**
 * Every OEM build NVIDIA lists. The silicon, memory and interconnect are
 * identical by NVIDIA's design; OEMs differentiate on storage, cooling,
 * chassis, warranty terms and remote management. We quote all of them, so
 * the honest answer to "which brand" is usually "whichever your procurement
 * and support arrangements already favour".
 */
export const dgxSparkOems: OemVariant[] = [
  {
    brand: "NVIDIA",
    model: "DGX Spark (Founders Edition)",
    note: "The reference build, with 4 TB self-encrypting NVMe. The baseline every OEM version is measured against.",
    brandSlug: "nvidia-malaysia",
  },
  {
    brand: "ASUS",
    model: "Ascent GX10",
    page: "asus-ascent-gx10-malaysia",
    note: "ASUS's build of the same GB10 platform, offered in more than one storage capacity.",
    brandSlug: "asus-business-malaysia",
  },
  {
    brand: "Dell Technologies",
    model: "Pro Max with GB10",
    page: "dell-pro-max-gb10-malaysia",
    note: "Dell's build, sold into the same fleet and support arrangements as the rest of a Dell estate.",
    brandSlug: "dell-technologies-malaysia",
  },
  {
    brand: "HP",
    model: "ZGX Nano AI Station (G1n)",
    page: "hp-zgx-nano-malaysia",
    note: "HP's Z-series take on the platform, aimed at workstation buyers already standardised on Z.",
    brandSlug: "hp-malaysia",
  },
  {
    brand: "Lenovo",
    model: "ThinkStation PGX",
    page: "lenovo-thinkstation-pgx-malaysia",
    note: "Lenovo's first GB10 workstation, carrying ThinkStation warranty and service options.",
    brandSlug: "lenovo-malaysia",
  },
  {
    brand: "Acer",
    model: "Veriton GN100 AI Mini Workstation",
    page: "acer-veriton-gn100-malaysia",
    note: "Acer's compact build of the same platform.",
    brandSlug: "acer-malaysia",
  },
  {
    brand: "Gigabyte",
    model: "AI TOP ATOM",
    note: "Gigabyte's GB10 build, listed by NVIDIA alongside the others.",
    brandSlug: "",
  },
  {
    brand: "MSI",
    model: "EdgeXpert (MS-C931)",
    note: "MSI's GB10 build, sold into industrial and edge deployments as well as the desk.",
    brandSlug: "",
  },
];

/**
 * PUBLIC RETAIL REFERENCE — NOT our quotation, and not a price we are offering.
 *
 * WHY THIS EXISTS (Jazz, 2026-09-12: publish the public retail price range).
 * Every price-intent query in the DGX cluster earned ZERO clicks despite ranking
 * on page 1 — including two where we sit at position 1.0 (`asus ascent gx10 price
 * malaysia`, `nvidia rtx spark laptop price`). Cause found on the live MY SERP:
 * Google was quoting our own FAQ back as the snippet, and it read "We do not
 * publish prices." Directly above it the AI Overview listed the whole market in
 * Ringgit. A searcher who types "price" and reads a refusal does not click.
 *
 * THE RULE THIS DOES NOT BREAK. The standing enterprise rule — no prices, no stock
 * claims, no lead times, quote-driven only — stays intact, because this is not our
 * price. It is what the hardware is publicly listed at by a Malaysian retailer,
 * attributed and dated, the way any market reference is. Our own number still only
 * appears in a quotation.
 *
 * ACCURACY. Figures read from a Malaysian retailer's public product feed on the
 * date below, rounded OUTWARDS so ordinary price movement does not make them wrong. Do not tighten the
 * rounding — precision here is a liability, not a feature, and a stale exact figure
 * is a false price claim where a stale range is merely old. Re-read the feed and
 * bump `retrieved` when it drifts; the range is referenced by both the hub FAQ and
 * the per-model FAQ factory in dgx-spark-models.ts, so this is the only edit.
 */
export const dgxSparkMarketPrice = {
  retrieved: "September 2026",
  /**
   * PUBLIC attribution — deliberately UNNAMED (Jazz, 2026-09-12).
   *
   * This shipped naming the retailer the figures were read from. That was wrong:
   * it put a competing seller's brand on our own product pages, in two locales
   * and seven llms.txt files, handing them a free mention on pages a buyer reads
   * while deciding where to buy. Same rule as never naming the distributor.
   *
   * The provenance stays in this comment so the number is still traceable and
   * re-checkable; it does not go on the page. "Malaysian retail listings" is
   * accurate, sufficient for a market reference, and names nobody.
   *
   * DO NOT re-add a retailer name, a `sourceUrl`, or a link to any of them here
   * or in any llms.txt. Verify against the live listings when refreshing, then
   * publish only the range.
   */
  source: "Malaysian retail listings",
  /** Rounded outwards from RM24,175–RM35,999 across 12 listed variants. */
  low: "RM24,000",
  high: "RM36,000",
  /** Indicative entry price per OEM, lowest listed variant. Storage tier is what moves it. */
  byBrand: {
    "Lenovo ThinkStation PGX": "RM24,400",
    "HP ZGX Nano G1n": "RM24,600",
    "NVIDIA Founders Edition": "RM24,600",
    "MSI EdgeXpert": "RM25,400",
    "Dell Pro Max with GB10": "RM26,300",
    "ASUS Ascent GX10": "RM26,700",
  } as Record<string, string>,
};

/** One sentence, reused wherever a price question is answered. Leads with the number. */
export const dgxSparkPriceAnswer =
  `In Malaysia the DGX Spark family is listed publicly at roughly ${dgxSparkMarketPrice.low} to ${dgxSparkMarketPrice.high}, depending on the OEM build and storage tier (${dgxSparkMarketPrice.source}, ${dgxSparkMarketPrice.retrieved}).`;

export const dgxSparkFaqs = [
  {
    question: "Where can I buy NVIDIA DGX Spark in Malaysia?",
    answer:
      "Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia, supplying both the NVIDIA Founders Edition and every OEM build — ASUS Ascent GX10, Dell Pro Max with GB10, HP ZGX Nano, Lenovo ThinkStation PGX, Acer Veriton GN100, Gigabyte AI TOP ATOM and MSI. Send the model and quantity through the enquiry form and we return a quotation in Ringgit with SST treatment stated and a MyInvois e-invoice on purchase.",
  },
  {
    // Leads with the number ON PURPOSE — this answer is what Google renders as our
    // snippet on "dgx spark price malaysia". It previously opened "We do not publish
    // prices" and earned zero clicks from page 1. See dgxSparkMarketPrice above.
    question: "How much does a DGX Spark cost in Malaysia?",
    answer:
      `${dgxSparkPriceAnswer} Storage is what moves it most — the same machine at 4 TB can sit several thousand Ringgit above its 1 TB version, and raw compute is identical across every OEM badge. We quote against your configuration rather than a shelf price, because a quotation carries SST treatment, a MyInvois e-invoice, warranty terms and delivery, and because allocation on these parts changes. Send the model and capacity and we come back in Ringgit, usually within two working days.`,
  },
  {
    question: "What is the difference between the OEM versions of DGX Spark?",
    answer:
      "Less than most buyers expect. NVIDIA fixes the GB10 superchip, the 128 GB of unified memory and the ConnectX-7 networking across every build, so raw compute is the same whichever badge is on the front. OEMs differentiate on storage capacity, cooling and acoustics, chassis design, warranty terms and remote management. In practice the right choice is usually whichever vendor your existing support and procurement arrangements already cover.",
  },
  {
    question: "Is DGX Spark the same thing as RTX Spark?",
    answer:
      "No — they are two different NVIDIA products with confusingly similar names, and buyers mix them up constantly. DGX Spark is the GB10 Grace Blackwell developer workstation on this page: it runs NVIDIA DGX OS, which is Linux, and it exists to hold and fine-tune large models locally. RTX Spark is NVIDIA's SoC family for Windows-on-Arm AI PCs, announced at Computex 2026 and built into laptops and desktops by the usual PC makers. The silicon is related, but one is a developer workstation and the other is a personal computer. If you are shopping for a laptop, you want RTX Spark; if you want to run a 200-billion-parameter model on your own desk, you want DGX Spark. Tell us the workload and we will tell you which.",
  },
  {
    question: "Is DGX Spark a replacement for a data-centre GPU server?",
    answer:
      "No, and it is worth being clear about that before you buy. DGX Spark has 128 GB of unified memory at 273 GB/s. That is a lot of capacity for the size and price class, which is what makes it excellent for prototyping, fine-tuning up to about 70 billion parameters and running models locally. But memory bandwidth, not capacity, is what governs large-model inference throughput, and a data-centre GPU has several times more. If your goal is high-volume production inference for many concurrent users, a Spark is the wrong tool and we will tell you so.",
  },
  {
    question: "Can I link two DGX Spark units together?",
    answer:
      "Yes. The ConnectX-7 networking at 200 Gbps exists for exactly this, and NVIDIA states that two connected units work with models up to 405 billion parameters. If that is your plan, tell us at enquiry stage so the quotation includes the cabling and the second unit is matched.",
  },
  {
    question: "Do I need a DGX Spark, or should I just use cloud GPUs?",
    answer:
      "It depends on utilisation and data residency, not on preference. A Spark is capital you own and can run at full utilisation without metering, which suits teams developing continuously or handling data that should not leave the building under PDPA. Cloud suits bursty workloads and anything needing more bandwidth than a Spark has. We build and operate AI systems in production, so we can tell you which case you are in before you commit to hardware.",
  },
  {
    question: "Does the quotation include SST and a MyInvois e-invoice?",
    answer:
      "Yes. Quotations are issued in Ringgit with SST treatment stated, purchase orders run on standard local terms, and invoices are issued through MyInvois in line with Malaysia's e-invoicing requirements.",
  },
  {
    question: "Can Anchor Sprint help set it up and actually use it?",
    answer:
      "Yes, and that is the part most resellers cannot do. We are Certified in the Claude Partner Network and we build agentic AI systems in production. Supplying the box is the easy half; getting a model serving something useful behind your own network is the half that decides whether the purchase pays for itself.",
  },
];

/**
 * Time-sensitive promotional note. Deliberately carries NO price and NO
 * discount figure — the standing rule is quote-driven only — and is dated so
 * it is obvious when it goes stale. Confirm with the distributor before
 * extending the date.
 */
export const dgxSparkPromo = {
  active: true,
  asOf: "August 2026",
  body:
    "The OEM builds from Dell, Lenovo, ASUS, Acer and HP are on promotional pricing through our distribution channel this period. Promotional windows on GB10 systems are short and allocation-dependent — ask for current numbers before committing to a budget.",
};
